/**
 * Budget labels — turn the internal band into a real, city-specific number.
 *
 * Why this exists:
 *   Users told us "Tight / Moderate / Stretch" is meaningless. They want to
 *   see what their money actually buys — and the number depends on three
 *   axes the app already owns: city (HYD vs MEL), purchase mode (Buy vs
 *   Rent), and property type (House vs Apartment vs Any).
 *
 *   This module never changes the internal `BudgetBand` enum. Classifier,
 *   shortlist, scoring — all untouched. Only the user-facing label changes.
 *
 * Design rules:
 *   - Pure functions. No DOM, no storage reads, no side effects.
 *   - Ranges are hand-picked to match the `priceRange` strings already in
 *     the verdicts datasets, so a user flipping to "Stretch" and then
 *     tapping a pin does not see two contradictory numbers.
 *   - "Any" property-type defaults to the House range, because Houses
 *     dominate most suburb price distributions in both cities. If the user
 *     flips to Apartment, the range softens (apartments are cheaper at the
 *     same band).
 *   - If the caller can't tell us Buy vs Rent (they pass `undefined`), we
 *     fall back to Buy — that's the decision most users start with.
 */

import type { BudgetBand } from "./budget-band";
import type { PurchaseMode } from "./purchase-mode";
import type { PropertyType } from "./property-type";
import type { SuburbPriceData } from "../data/verdicts";

export type CityCode = "hyd" | "mel" | "blr" | "syd";

export interface BudgetFormatCtx {
  city: CityCode;
  mode: PurchaseMode;
  type: PropertyType;
}

/* -------------------------------------------------------------------------- */
/*  Range tables                                                              */
/* -------------------------------------------------------------------------- */

/**
 * Price ranges per (city, mode, type, band). Numbers are approximate bands —
 * the point is "users feel this is the right ballpark", not pricing truth.
 *
 * Keep the tables literal rather than derived; a future change to, say, the
 * Melbourne rental market is a one-line edit rather than a formula audit.
 */
const RANGES: Record<
  CityCode,
  Record<
    PurchaseMode,
    Record<"house" | "apartment", Record<Exclude<BudgetBand, "none">, string>>
  >
> = {
  mel: {
    buy: {
      house: {
        tight: "A$600k–900k",
        moderate: "A$900k–1.5M",
        stretch: "A$1.5M–3M+",
      },
      apartment: {
        tight: "A$350k–550k",
        moderate: "A$550k–900k",
        stretch: "A$900k–1.6M+",
      },
    },
    rent: {
      house: {
        tight: "$450–600/wk",
        moderate: "$600–850/wk",
        stretch: "$850–1400/wk",
      },
      apartment: {
        tight: "$350–500/wk",
        moderate: "$500–700/wk",
        stretch: "$700–1100/wk",
      },
    },
  },
  hyd: {
    buy: {
      house: {
        tight: "₹60L–1.2Cr",
        moderate: "₹1.2Cr–2.5Cr",
        stretch: "₹2.5Cr–6Cr+",
      },
      apartment: {
        tight: "₹50L–90L",
        moderate: "₹90L–1.8Cr",
        stretch: "₹1.8Cr–4Cr+",
      },
    },
    rent: {
      house: {
        tight: "₹20k–35k/mo",
        moderate: "₹35k–60k/mo",
        stretch: "₹60k–1.2L/mo",
      },
      apartment: {
        tight: "₹15k–25k/mo",
        moderate: "₹25k–45k/mo",
        stretch: "₹45k–90k/mo",
      },
    },
  },
  blr: {
    buy: {
      house: {
        tight: "₹70L–1.3Cr",
        moderate: "₹1.3Cr–2.6Cr",
        stretch: "₹2.6Cr–6Cr+",
      },
      apartment: {
        tight: "₹55L–95L",
        moderate: "₹95L–1.85Cr",
        stretch: "₹1.85Cr–4Cr+",
      },
    },
    rent: {
      house: {
        tight: "₹25k–40k/mo",
        moderate: "₹40k–65k/mo",
        stretch: "₹65k–1.3L/mo",
      },
      apartment: {
        tight: "₹18k–28k/mo",
        moderate: "₹28k–50k/mo",
        stretch: "₹50k–95k/mo",
      },
    },
  },
  syd: {
    buy: {
      house: {
        tight: "A$900k–1.4M",
        moderate: "A$1.4M–2.2M",
        stretch: "A$2.2M–5M+",
      },
      apartment: {
        tight: "A$550k–800k",
        moderate: "A$800k–1.3M",
        stretch: "A$1.3M–2.5M+",
      },
    },
    rent: {
      house: {
        tight: "$600–800/wk",
        moderate: "$800–1100/wk",
        stretch: "$1100–1800/wk",
      },
      apartment: {
        tight: "$500–650/wk",
        moderate: "$650–900/wk",
        stretch: "$900–1500/wk",
      },
    },
  },
};

/* -------------------------------------------------------------------------- */
/*  Buckets — finer sub-ranges for the budget control (UI only).              */
/*                                                                            */
/*  Users said the 3-band picker (tight/moderate/stretch) was too coarse —    */
/*  a 300 K band doesn't feel like "my budget". These buckets split each      */
/*  band roughly in half so the user can pick a tighter range, and each       */
/*  bucket still maps to one of the existing BudgetBand values internally.    */
/*  Scoring / filtering logic remains band-based; only the control UI + the   */
/*  label shown on the active pill change.                                    */
/* -------------------------------------------------------------------------- */

export type BudgetBucket =
  | "any"
  | "b0" | "b1" | "b2" | "b3"   // sub-splits of "tight"
  | "b4" | "b5" | "b6"          // sub-splits of "moderate"
  | "b7" | "b8";                // sub-splits of "stretch"

interface BucketDef {
  label: string;
  band: BudgetBand;
  /** Numeric lower bound in the ctx's unit (k for Buy, $/wk for Rent-MEL,
   *  L for Buy-HYD, k/mo for Rent-HYD). */
  lo: number;
  /** Numeric upper bound. Use a large sentinel for open-ended "+" buckets. */
  hi: number;
}

const OPEN_UPPER_MEL_BUY = 5000;      // A$5M cap for open-ended display
const OPEN_UPPER_MEL_RENT = 1800;     // $1800/wk cap
const OPEN_UPPER_HYD_BUY = 700;       // ₹7Cr (700L) cap
const OPEN_UPPER_HYD_RENT = 180;      // ₹180k/mo cap
const OPEN_UPPER_BLR_BUY = 700;       // ₹7Cr (700L) cap — same units as HYD
const OPEN_UPPER_BLR_RENT = 180;      // ₹180k/mo cap — same units as HYD
const OPEN_UPPER_SYD_BUY = 8000;      // A$8M cap — Sydney premium tail is taller than Melbourne
const OPEN_UPPER_SYD_RENT = 2500;     // $2500/wk cap — Sydney premium tail

const BUCKETS: Record<
  CityCode,
  Record<PurchaseMode, Record<"house" | "apartment", Record<BudgetBucket, BucketDef>>>
> = {
  mel: {
    buy: {
      house: {
        any: { label: "Any",          band: "none",     lo: 0,    hi: OPEN_UPPER_MEL_BUY },
        b0:  { label: "Up to A$600k", band: "tight",    lo: 0,    hi: 600 },
        b1:  { label: "A$600–700k",   band: "tight",    lo: 600,  hi: 700 },
        b2:  { label: "A$700–800k",   band: "tight",    lo: 700,  hi: 800 },
        b3:  { label: "A$800–900k",   band: "tight",    lo: 800,  hi: 900 },
        b4:  { label: "A$900k–1M",    band: "moderate", lo: 900,  hi: 1000 },
        b5:  { label: "A$1–1.2M",     band: "moderate", lo: 1000, hi: 1200 },
        b6:  { label: "A$1.2–1.5M",   band: "moderate", lo: 1200, hi: 1500 },
        b7:  { label: "A$1.5–2M",     band: "stretch",  lo: 1500, hi: 2000 },
        b8:  { label: "A$2M+",        band: "stretch",  lo: 2000, hi: OPEN_UPPER_MEL_BUY },
      },
      apartment: {
        any: { label: "Any",          band: "none",     lo: 0,   hi: OPEN_UPPER_MEL_BUY },
        b0:  { label: "Up to A$400k", band: "tight",    lo: 0,   hi: 400 },
        b1:  { label: "A$400–450k",   band: "tight",    lo: 400, hi: 450 },
        b2:  { label: "A$450–500k",   band: "tight",    lo: 450, hi: 500 },
        b3:  { label: "A$500–550k",   band: "tight",    lo: 500, hi: 550 },
        b4:  { label: "A$550–650k",   band: "moderate", lo: 550, hi: 650 },
        b5:  { label: "A$650–780k",   band: "moderate", lo: 650, hi: 780 },
        b6:  { label: "A$780k–900k",  band: "moderate", lo: 780, hi: 900 },
        b7:  { label: "A$900k–1.2M",  band: "stretch",  lo: 900, hi: 1200 },
        b8:  { label: "A$1.2M+",      band: "stretch",  lo: 1200, hi: OPEN_UPPER_MEL_BUY },
      },
    },
    rent: {
      house: {
        any: { label: "Any",           band: "none",     lo: 0,   hi: OPEN_UPPER_MEL_RENT },
        b0:  { label: "Up to $475/wk", band: "tight",    lo: 0,   hi: 475 },
        b1:  { label: "$475–525/wk",   band: "tight",    lo: 475, hi: 525 },
        b2:  { label: "$525–575/wk",   band: "tight",    lo: 525, hi: 575 },
        b3:  { label: "$575–600/wk",   band: "tight",    lo: 575, hi: 600 },
        b4:  { label: "$600–700/wk",   band: "moderate", lo: 600, hi: 700 },
        b5:  { label: "$700–775/wk",   band: "moderate", lo: 700, hi: 775 },
        b6:  { label: "$775–850/wk",   band: "moderate", lo: 775, hi: 850 },
        b7:  { label: "$850–1100/wk",  band: "stretch",  lo: 850, hi: 1100 },
        b8:  { label: "$1100+/wk",     band: "stretch",  lo: 1100, hi: OPEN_UPPER_MEL_RENT },
      },
      apartment: {
        any: { label: "Any",           band: "none",     lo: 0,   hi: OPEN_UPPER_MEL_RENT },
        b0:  { label: "Up to $400/wk", band: "tight",    lo: 0,   hi: 400 },
        b1:  { label: "$400–450/wk",   band: "tight",    lo: 400, hi: 450 },
        b2:  { label: "$450–475/wk",   band: "tight",    lo: 450, hi: 475 },
        b3:  { label: "$475–500/wk",   band: "tight",    lo: 475, hi: 500 },
        b4:  { label: "$500–575/wk",   band: "moderate", lo: 500, hi: 575 },
        b5:  { label: "$575–650/wk",   band: "moderate", lo: 575, hi: 650 },
        b6:  { label: "$650–700/wk",   band: "moderate", lo: 650, hi: 700 },
        b7:  { label: "$700–900/wk",   band: "stretch",  lo: 700, hi: 900 },
        b8:  { label: "$900+/wk",      band: "stretch",  lo: 900, hi: OPEN_UPPER_MEL_RENT },
      },
    },
  },
  hyd: {
    buy: {
      // Unit = Lakh (100L = 1Cr). 60L = 0.6Cr; 120L = 1.2Cr; etc.
      house: {
        any: { label: "Any",         band: "none",     lo: 0,   hi: OPEN_UPPER_HYD_BUY },
        b0:  { label: "Up to ₹80L",  band: "tight",    lo: 0,   hi: 80 },
        b1:  { label: "₹80L–1Cr",    band: "tight",    lo: 80,  hi: 100 },
        b2:  { label: "₹1–1.1Cr",    band: "tight",    lo: 100, hi: 110 },
        b3:  { label: "₹1.1–1.2Cr",  band: "tight",    lo: 110, hi: 120 },
        b4:  { label: "₹1.2–1.5Cr",  band: "moderate", lo: 120, hi: 150 },
        b5:  { label: "₹1.5–1.8Cr",  band: "moderate", lo: 150, hi: 180 },
        b6:  { label: "₹1.8–2.5Cr",  band: "moderate", lo: 180, hi: 250 },
        b7:  { label: "₹2.5–4Cr",    band: "stretch",  lo: 250, hi: 400 },
        b8:  { label: "₹4Cr+",       band: "stretch",  lo: 400, hi: OPEN_UPPER_HYD_BUY },
      },
      apartment: {
        any: { label: "Any",         band: "none",     lo: 0,   hi: OPEN_UPPER_HYD_BUY },
        b0:  { label: "Up to ₹60L",  band: "tight",    lo: 0,   hi: 60 },
        b1:  { label: "₹60–70L",     band: "tight",    lo: 60,  hi: 70 },
        b2:  { label: "₹70–80L",     band: "tight",    lo: 70,  hi: 80 },
        b3:  { label: "₹80–90L",     band: "tight",    lo: 80,  hi: 90 },
        b4:  { label: "₹90L–1.2Cr",  band: "moderate", lo: 90,  hi: 120 },
        b5:  { label: "₹1.2–1.5Cr",  band: "moderate", lo: 120, hi: 150 },
        b6:  { label: "₹1.5–1.8Cr",  band: "moderate", lo: 150, hi: 180 },
        b7:  { label: "₹1.8–2.8Cr",  band: "stretch",  lo: 180, hi: 280 },
        b8:  { label: "₹2.8Cr+",     band: "stretch",  lo: 280, hi: OPEN_UPPER_HYD_BUY },
      },
    },
    rent: {
      // Unit = k/mo (thousands of rupees per month).
      house: {
        any: { label: "Any",          band: "none",     lo: 0,  hi: OPEN_UPPER_HYD_RENT },
        b0:  { label: "Up to ₹25k/mo", band: "tight",   lo: 0,  hi: 25 },
        b1:  { label: "₹25–28k/mo",   band: "tight",    lo: 25, hi: 28 },
        b2:  { label: "₹28–32k/mo",   band: "tight",    lo: 28, hi: 32 },
        b3:  { label: "₹32–35k/mo",   band: "tight",    lo: 32, hi: 35 },
        b4:  { label: "₹35–45k/mo",   band: "moderate", lo: 35, hi: 45 },
        b5:  { label: "₹45–55k/mo",   band: "moderate", lo: 45, hi: 55 },
        b6:  { label: "₹55–60k/mo",   band: "moderate", lo: 55, hi: 60 },
        b7:  { label: "₹60–90k/mo",   band: "stretch",  lo: 60, hi: 90 },
        b8:  { label: "₹90k+/mo",     band: "stretch",  lo: 90, hi: OPEN_UPPER_HYD_RENT },
      },
      apartment: {
        any: { label: "Any",          band: "none",     lo: 0,  hi: OPEN_UPPER_HYD_RENT },
        b0:  { label: "Up to ₹18k/mo", band: "tight",   lo: 0,  hi: 18 },
        b1:  { label: "₹18–20k/mo",   band: "tight",    lo: 18, hi: 20 },
        b2:  { label: "₹20–22k/mo",   band: "tight",    lo: 20, hi: 22 },
        b3:  { label: "₹22–25k/mo",   band: "tight",    lo: 22, hi: 25 },
        b4:  { label: "₹25–32k/mo",   band: "moderate", lo: 25, hi: 32 },
        b5:  { label: "₹32–40k/mo",   band: "moderate", lo: 32, hi: 40 },
        b6:  { label: "₹40–45k/mo",   band: "moderate", lo: 40, hi: 45 },
        b7:  { label: "₹45–70k/mo",   band: "stretch",  lo: 45, hi: 70 },
        b8:  { label: "₹70k+/mo",     band: "stretch",  lo: 70, hi: OPEN_UPPER_HYD_RENT },
      },
    },
  },
  blr: {
    // Unit = Lakh (100L = 1Cr). Calibrated against MagicBricks / 99acres BLR
    // 2025 — slightly above HYD on premium central pockets (Indiranagar,
    // Koramangala) and similar on tech-corridor stock (Whitefield, EC).
    buy: {
      house: {
        any: { label: "Any",         band: "none",     lo: 0,   hi: OPEN_UPPER_BLR_BUY },
        b0:  { label: "Up to ₹85L",  band: "tight",    lo: 0,   hi: 85 },
        b1:  { label: "₹85L–1Cr",    band: "tight",    lo: 85,  hi: 100 },
        b2:  { label: "₹1–1.15Cr",   band: "tight",    lo: 100, hi: 115 },
        b3:  { label: "₹1.15–1.3Cr", band: "tight",    lo: 115, hi: 130 },
        b4:  { label: "₹1.3–1.6Cr",  band: "moderate", lo: 130, hi: 160 },
        b5:  { label: "₹1.6–2Cr",    band: "moderate", lo: 160, hi: 200 },
        b6:  { label: "₹2–2.6Cr",    band: "moderate", lo: 200, hi: 260 },
        b7:  { label: "₹2.6–4Cr",    band: "stretch",  lo: 260, hi: 400 },
        b8:  { label: "₹4Cr+",       band: "stretch",  lo: 400, hi: OPEN_UPPER_BLR_BUY },
      },
      apartment: {
        any: { label: "Any",         band: "none",     lo: 0,   hi: OPEN_UPPER_BLR_BUY },
        b0:  { label: "Up to ₹65L",  band: "tight",    lo: 0,   hi: 65 },
        b1:  { label: "₹65–75L",     band: "tight",    lo: 65,  hi: 75 },
        b2:  { label: "₹75–85L",     band: "tight",    lo: 75,  hi: 85 },
        b3:  { label: "₹85–95L",     band: "tight",    lo: 85,  hi: 95 },
        b4:  { label: "₹95L–1.25Cr", band: "moderate", lo: 95,  hi: 125 },
        b5:  { label: "₹1.25–1.55Cr", band: "moderate", lo: 125, hi: 155 },
        b6:  { label: "₹1.55–1.85Cr", band: "moderate", lo: 155, hi: 185 },
        b7:  { label: "₹1.85–2.8Cr", band: "stretch",  lo: 185, hi: 280 },
        b8:  { label: "₹2.8Cr+",     band: "stretch",  lo: 280, hi: OPEN_UPPER_BLR_BUY },
      },
    },
    rent: {
      // Unit = k/mo. BLR rents skew slightly higher than HYD on tech-corridor
      // stock (4-day RTO mandates have tightened supply 2024–25).
      house: {
        any: { label: "Any",          band: "none",     lo: 0,  hi: OPEN_UPPER_BLR_RENT },
        b0:  { label: "Up to ₹28k/mo", band: "tight",   lo: 0,  hi: 28 },
        b1:  { label: "₹28–32k/mo",   band: "tight",    lo: 28, hi: 32 },
        b2:  { label: "₹32–36k/mo",   band: "tight",    lo: 32, hi: 36 },
        b3:  { label: "₹36–40k/mo",   band: "tight",    lo: 36, hi: 40 },
        b4:  { label: "₹40–50k/mo",   band: "moderate", lo: 40, hi: 50 },
        b5:  { label: "₹50–58k/mo",   band: "moderate", lo: 50, hi: 58 },
        b6:  { label: "₹58–65k/mo",   band: "moderate", lo: 58, hi: 65 },
        b7:  { label: "₹65–95k/mo",   band: "stretch",  lo: 65, hi: 95 },
        b8:  { label: "₹95k+/mo",     band: "stretch",  lo: 95, hi: OPEN_UPPER_BLR_RENT },
      },
      apartment: {
        any: { label: "Any",          band: "none",     lo: 0,  hi: OPEN_UPPER_BLR_RENT },
        b0:  { label: "Up to ₹20k/mo", band: "tight",   lo: 0,  hi: 20 },
        b1:  { label: "₹20–22k/mo",   band: "tight",    lo: 20, hi: 22 },
        b2:  { label: "₹22–25k/mo",   band: "tight",    lo: 22, hi: 25 },
        b3:  { label: "₹25–28k/mo",   band: "tight",    lo: 25, hi: 28 },
        b4:  { label: "₹28–35k/mo",   band: "moderate", lo: 28, hi: 35 },
        b5:  { label: "₹35–42k/mo",   band: "moderate", lo: 35, hi: 42 },
        b6:  { label: "₹42–50k/mo",   band: "moderate", lo: 42, hi: 50 },
        b7:  { label: "₹50–72k/mo",   band: "stretch",  lo: 50, hi: 72 },
        b8:  { label: "₹72k+/mo",     band: "stretch",  lo: 72, hi: OPEN_UPPER_BLR_RENT },
      },
    },
  },
  syd: {
    // Unit = A$ thousands (so 1400 = A$1.4M). Calibrated against Domain /
    // realestate.com.au Sydney 2024–25 medians — Sydney sits ~50–70% above
    // Melbourne on house, ~40% above on apartment.
    buy: {
      house: {
        any: { label: "Any",            band: "none",     lo: 0,    hi: OPEN_UPPER_SYD_BUY },
        b0:  { label: "Up to A$1M",     band: "tight",    lo: 0,    hi: 1000 },
        b1:  { label: "A$1–1.15M",      band: "tight",    lo: 1000, hi: 1150 },
        b2:  { label: "A$1.15–1.3M",    band: "tight",    lo: 1150, hi: 1300 },
        b3:  { label: "A$1.3–1.4M",     band: "tight",    lo: 1300, hi: 1400 },
        b4:  { label: "A$1.4–1.65M",    band: "moderate", lo: 1400, hi: 1650 },
        b5:  { label: "A$1.65–1.9M",    band: "moderate", lo: 1650, hi: 1900 },
        b6:  { label: "A$1.9–2.2M",     band: "moderate", lo: 1900, hi: 2200 },
        b7:  { label: "A$2.2–3M",       band: "stretch",  lo: 2200, hi: 3000 },
        b8:  { label: "A$3M+",          band: "stretch",  lo: 3000, hi: OPEN_UPPER_SYD_BUY },
      },
      apartment: {
        any: { label: "Any",            band: "none",     lo: 0,    hi: OPEN_UPPER_SYD_BUY },
        b0:  { label: "Up to A$600k",   band: "tight",    lo: 0,    hi: 600 },
        b1:  { label: "A$600–675k",     band: "tight",    lo: 600,  hi: 675 },
        b2:  { label: "A$675–740k",     band: "tight",    lo: 675,  hi: 740 },
        b3:  { label: "A$740–800k",     band: "tight",    lo: 740,  hi: 800 },
        b4:  { label: "A$800–950k",     band: "moderate", lo: 800,  hi: 950 },
        b5:  { label: "A$950k–1.1M",    band: "moderate", lo: 950,  hi: 1100 },
        b6:  { label: "A$1.1–1.3M",     band: "moderate", lo: 1100, hi: 1300 },
        b7:  { label: "A$1.3–1.7M",     band: "stretch",  lo: 1300, hi: 1700 },
        b8:  { label: "A$1.7M+",        band: "stretch",  lo: 1700, hi: OPEN_UPPER_SYD_BUY },
      },
    },
    rent: {
      house: {
        any: { label: "Any",           band: "none",     lo: 0,    hi: OPEN_UPPER_SYD_RENT },
        b0:  { label: "Up to $625/wk", band: "tight",    lo: 0,    hi: 625 },
        b1:  { label: "$625–700/wk",   band: "tight",    lo: 625,  hi: 700 },
        b2:  { label: "$700–750/wk",   band: "tight",    lo: 700,  hi: 750 },
        b3:  { label: "$750–800/wk",   band: "tight",    lo: 750,  hi: 800 },
        b4:  { label: "$800–900/wk",   band: "moderate", lo: 800,  hi: 900 },
        b5:  { label: "$900–1000/wk",  band: "moderate", lo: 900,  hi: 1000 },
        b6:  { label: "$1000–1100/wk", band: "moderate", lo: 1000, hi: 1100 },
        b7:  { label: "$1100–1400/wk", band: "stretch",  lo: 1100, hi: 1400 },
        b8:  { label: "$1400+/wk",     band: "stretch",  lo: 1400, hi: OPEN_UPPER_SYD_RENT },
      },
      apartment: {
        any: { label: "Any",           band: "none",     lo: 0,   hi: OPEN_UPPER_SYD_RENT },
        b0:  { label: "Up to $525/wk", band: "tight",    lo: 0,   hi: 525 },
        b1:  { label: "$525–575/wk",   band: "tight",    lo: 525, hi: 575 },
        b2:  { label: "$575–625/wk",   band: "tight",    lo: 575, hi: 625 },
        b3:  { label: "$625–650/wk",   band: "tight",    lo: 625, hi: 650 },
        b4:  { label: "$650–750/wk",   band: "moderate", lo: 650, hi: 750 },
        b5:  { label: "$750–825/wk",   band: "moderate", lo: 750, hi: 825 },
        b6:  { label: "$825–900/wk",   band: "moderate", lo: 825, hi: 900 },
        b7:  { label: "$900–1200/wk",  band: "stretch",  lo: 900, hi: 1200 },
        b8:  { label: "$1200+/wk",     band: "stretch",  lo: 1200, hi: OPEN_UPPER_SYD_RENT },
      },
    },
  },
};

/** All bucket keys in display order. */
export const BUDGET_BUCKETS: readonly BudgetBucket[] = [
  "any", "b0", "b1", "b2", "b3", "b4", "b5", "b6", "b7", "b8",
] as const;

/** Look up the (label, band, lo, hi) definition for a bucket. */
export function budgetBucketDef(
  ctx: BudgetFormatCtx,
  bucket: BudgetBucket
): BucketDef {
  const effType: "house" | "apartment" =
    ctx.type === "apartment" ? "apartment" : "house";
  return BUCKETS[ctx.city][ctx.mode][effType][bucket];
}

/** Reverse-lookup: given a band, return the first bucket that maps to it. */
export function firstBucketForBand(
  ctx: BudgetFormatCtx,
  band: BudgetBand
): BudgetBucket {
  for (const b of BUDGET_BUCKETS) {
    if (budgetBucketDef(ctx, b).band === band) return b;
  }
  return "any";
}

/* -------------------------------------------------------------------------- */
/*  Numeric bounds for bands — used by the clamp logic below                  */
/* -------------------------------------------------------------------------- */

interface NumRange { lo: number; hi: number; }

const BAND_BOUNDS: Record<
  CityCode,
  Record<PurchaseMode, Record<"house" | "apartment", Record<Exclude<BudgetBand, "none">, NumRange>>>
> = {
  mel: {
    buy: {
      house:     { tight: { lo: 600, hi: 900 }, moderate: { lo: 900, hi: 1500 }, stretch: { lo: 1500, hi: OPEN_UPPER_MEL_BUY } },
      apartment: { tight: { lo: 350, hi: 550 }, moderate: { lo: 550, hi: 900 },  stretch: { lo: 900, hi: OPEN_UPPER_MEL_BUY } },
    },
    rent: {
      house:     { tight: { lo: 450, hi: 600 }, moderate: { lo: 600, hi: 850 }, stretch: { lo: 850, hi: OPEN_UPPER_MEL_RENT } },
      apartment: { tight: { lo: 350, hi: 500 }, moderate: { lo: 500, hi: 700 }, stretch: { lo: 700, hi: OPEN_UPPER_MEL_RENT } },
    },
  },
  hyd: {
    buy: {
      house:     { tight: { lo: 60,  hi: 120 }, moderate: { lo: 120, hi: 250 }, stretch: { lo: 250, hi: OPEN_UPPER_HYD_BUY } },
      apartment: { tight: { lo: 50,  hi: 90 },  moderate: { lo: 90,  hi: 180 }, stretch: { lo: 180, hi: OPEN_UPPER_HYD_BUY } },
    },
    rent: {
      house:     { tight: { lo: 20,  hi: 35 },  moderate: { lo: 35,  hi: 60 },  stretch: { lo: 60,  hi: OPEN_UPPER_HYD_RENT } },
      apartment: { tight: { lo: 15,  hi: 25 },  moderate: { lo: 25,  hi: 45 },  stretch: { lo: 45,  hi: OPEN_UPPER_HYD_RENT } },
    },
  },
  blr: {
    buy: {
      house:     { tight: { lo: 70,  hi: 130 }, moderate: { lo: 130, hi: 260 }, stretch: { lo: 260, hi: OPEN_UPPER_BLR_BUY } },
      apartment: { tight: { lo: 55,  hi: 95 },  moderate: { lo: 95,  hi: 185 }, stretch: { lo: 185, hi: OPEN_UPPER_BLR_BUY } },
    },
    rent: {
      house:     { tight: { lo: 25,  hi: 40 },  moderate: { lo: 40,  hi: 65 },  stretch: { lo: 65,  hi: OPEN_UPPER_BLR_RENT } },
      apartment: { tight: { lo: 18,  hi: 28 },  moderate: { lo: 28,  hi: 50 },  stretch: { lo: 50,  hi: OPEN_UPPER_BLR_RENT } },
    },
  },
  syd: {
    buy: {
      house:     { tight: { lo: 900, hi: 1400 }, moderate: { lo: 1400, hi: 2200 }, stretch: { lo: 2200, hi: OPEN_UPPER_SYD_BUY } },
      apartment: { tight: { lo: 550, hi: 800 },  moderate: { lo: 800,  hi: 1300 }, stretch: { lo: 1300, hi: OPEN_UPPER_SYD_BUY } },
    },
    rent: {
      house:     { tight: { lo: 600, hi: 800 },  moderate: { lo: 800,  hi: 1100 }, stretch: { lo: 1100, hi: OPEN_UPPER_SYD_RENT } },
      apartment: { tight: { lo: 500, hi: 650 },  moderate: { lo: 650,  hi: 900 },  stretch: { lo: 900,  hi: OPEN_UPPER_SYD_RENT } },
    },
  },
};

function effType(ctx: BudgetFormatCtx): "house" | "apartment" {
  return ctx.type === "apartment" ? "apartment" : "house";
}

function bandBounds(ctx: BudgetFormatCtx, band: BudgetBand): NumRange {
  if (band === "none") {
    // "Any" band spans everything — used only by caller fallbacks.
    const t = BAND_BOUNDS[ctx.city][ctx.mode][effType(ctx)].tight.lo;
    const s = BAND_BOUNDS[ctx.city][ctx.mode][effType(ctx)].stretch.hi;
    return { lo: t, hi: s };
  }
  return BAND_BOUNDS[ctx.city][ctx.mode][effType(ctx)][band];
}

/** Cap on displayed range width.
 *
 *  Product decision: the displayed range must feel like a *decision* —
 *  not a search band. Anything wider than A$100k (or equivalent ₹10L)
 *  reads as "the engine doesn't know" and drops trust. Rent ranges are
 *  capped tighter still — $150/wk already covers a meaningful decile.
 *
 *  Equivalents used:
 *    MEL buy  — A$100k
 *    MEL rent — $150/wk
 *    HYD buy  — ₹10L
 *    HYD rent — ₹10k/mo
 */
function maxDisplayWidth(ctx: BudgetFormatCtx): number {
  if (ctx.city === "mel") return ctx.mode === "buy" ? 100 : 150;
  if (ctx.city === "syd") return ctx.mode === "buy" ? 150 : 200;
  // hyd & blr share Indian-rupee units (Lakh for buy, k/mo for rent).
  return ctx.mode === "buy" ? 10 : 10;
}

/** Half-width of the window we build around a researched median when
 *  `priceData` is supplied. Narrower than maxDisplayWidth so the final
 *  clamp + intersect still has room to bite down on the user's bucket. */
function medianHalfWidth(ctx: BudgetFormatCtx): number {
  if (ctx.city === "mel") return ctx.mode === "buy" ? 50 : 75;
  if (ctx.city === "syd") return ctx.mode === "buy" ? 75 : 100;
  return ctx.mode === "buy" ? 5 : 5;
}

/**
 * Pick the median that best matches the user's (purchaseMode, propertyType)
 * context out of a `SuburbPriceData` record. Returns null if the record
 * doesn't carry the relevant median — caller falls back to band logic.
 *
 * Convention:
 *   - ctx.type === "apartment" → prefer apartment median, fall through to house
 *   - ctx.type === "house"     → prefer house median, fall through to apartment
 *   - ctx.type === "any"       → prefer house (dominates most suburb pockets),
 *                                fall through to apartment
 */
function pickMedian(
  ctx: BudgetFormatCtx,
  priceData: SuburbPriceData
): number | null {
  const isBuy = ctx.mode === "buy";
  const house = isBuy ? priceData.buyHouseMedian : priceData.rentHouseMedian;
  const apt = isBuy ? priceData.buyApartmentMedian : priceData.rentApartmentMedian;
  if (ctx.type === "apartment") return apt ?? house ?? null;
  return house ?? apt ?? null;
}

/**
 * Public wrapper over `pickMedian` — exposed so the shortlist eligibility
 * gate (`bucketFit` in auto-shortlist.ts) can reason about the exact
 * researched number without duplicating the type/mode precedence rules.
 */
export function pickPriceMedian(
  ctx: BudgetFormatCtx,
  priceData: SuburbPriceData
): number | null {
  return pickMedian(ctx, priceData);
}

/* -------------------------------------------------------------------------- */
/*  Formatter — turn a NumRange back into a display string per ctx.           */
/* -------------------------------------------------------------------------- */

function openUpper(ctx: BudgetFormatCtx): number {
  if (ctx.city === "mel") return ctx.mode === "buy" ? OPEN_UPPER_MEL_BUY : OPEN_UPPER_MEL_RENT;
  if (ctx.city === "syd") return ctx.mode === "buy" ? OPEN_UPPER_SYD_BUY : OPEN_UPPER_SYD_RENT;
  if (ctx.city === "blr") return ctx.mode === "buy" ? OPEN_UPPER_BLR_BUY : OPEN_UPPER_BLR_RENT;
  return ctx.mode === "buy" ? OPEN_UPPER_HYD_BUY : OPEN_UPPER_HYD_RENT;
}

/**
 * Render a single amount. `withPrefix=false` drops the currency prefix
 * so a range like "A$700k–900k" doesn't repeat "A$" on the second half.
 */
function formatAmount(ctx: BudgetFormatCtx, n: number, withPrefix: boolean): string {
  // Australian cities (mel, syd) — A$ for buy, $/wk for rent.
  if (ctx.city === "mel" || ctx.city === "syd") {
    if (ctx.mode === "rent") {
      const p = withPrefix ? "$" : "";
      return `${p}${Math.round(n)}/wk`;
    }
    const p = withPrefix ? "A$" : "";
    if (n < 1000) return `${p}${Math.round(n)}k`;
    const m = n / 1000;
    const s = Number.isInteger(m) ? m.toFixed(0) : m.toFixed(1);
    return `${p}${s}M`;
  }
  // Indian cities (hyd, blr) — ₹ Lakh for buy, ₹ k/mo for rent.
  if (ctx.mode === "rent") {
    const p = withPrefix ? "₹" : "";
    return `${p}${Math.round(n)}k/mo`;
  }
  const p = withPrefix ? "₹" : "";
  if (n < 100) return `${p}${Math.round(n)}L`;
  const cr = n / 100;
  const s = Number.isInteger(cr) ? cr.toFixed(0) : cr.toFixed(1);
  return `${p}${s}Cr`;
}

/**
 * Format a numeric range per ctx. Open upper produces an "N+" style.
 * Currency prefix appears once (on the low end) — e.g. "A$700k–900k".
 */
export function formatNumRange(
  ctx: BudgetFormatCtx,
  range: NumRange
): string {
  const openU = openUpper(ctx);
  if (range.hi >= openU) {
    return `${formatAmount(ctx, range.lo, true)}+`;
  }
  if (range.lo === 0) {
    return `Up to ${formatAmount(ctx, range.hi, true)}`;
  }
  return `${formatAmount(ctx, range.lo, true)}–${formatAmount(ctx, range.hi, false)}`;
}

/**
 * Rounding step used when collapsing a range to a single "decision price".
 * Tuned per ctx so midpoints land on clean numbers (no "A$652k" or
 * "₹1.27Cr" artifacts).
 */
function midpointStep(ctx: BudgetFormatCtx): number {
  if (ctx.city === "mel") return ctx.mode === "rent" ? 25 : 50;   // $25/wk  |  A$50k
  if (ctx.city === "syd") return ctx.mode === "rent" ? 25 : 100;  // $25/wk  |  A$100k (Sydney premium tail)
  // hyd & blr — Indian-rupee units.
  return ctx.mode === "rent" ? 2 : 5;       // ₹2k/mo  |  ₹5L
}

/**
 * Collapse a numeric range into a single "decision price" — the value
 * users really ask for ("what will I actually pay?"). Always prefixed
 * with "~" so the figure reads as an anchor, not a promise.
 *
 * Rules:
 *   - Closed range [lo, hi] → midpoint, rounded to a clean step per ctx.
 *   - Open upper (hi ≥ openUpper)  → "~{lo}" (no "+" — user wants one
 *     number; lo is a truthful floor for their budget).
 *   - Zero lower (lo === 0) → midpoint = hi / 2, rounded (rare; clamp
 *     typically makes "Up to X" cases fall through to the band fallback).
 */
export function formatMidpoint(
  ctx: BudgetFormatCtx,
  range: NumRange
): string {
  const openU = openUpper(ctx);
  if (range.hi >= openU) {
    return `~${formatAmount(ctx, range.lo, true)}`;
  }
  const step = midpointStep(ctx);
  const rawMid = (range.lo + range.hi) / 2;
  const mid = Math.round(rawMid / step) * step;
  return `~${formatAmount(ctx, mid, true)}`;
}

/**
 * Intersect the suburb's band with the user's bucket, then cap the
 * width. Returns null when there's no overlap — callers should hide
 * or fall back in that case. All other paths return a range narrow
 * enough to feel like the user's chosen budget.
 *
 * When `priceData` carries a researched median for the current
 * (purchaseMode, propertyType) combo, we anchor the range on that
 * median instead of on the coarse band. The result is a much tighter
 * window around the real current-market number — sourced from
 * property.com.au / realestate.com.au / CoreLogic for MEL and from
 * MagicBricks / 99acres for HYD. Band logic remains the fallback so
 * suburbs without researched data still return a usable range.
 *
 * Precedence:
 *   1. If priceData has a matching median → build ±halfWidth window
 *      around it, then intersect with the user's bucket, then cap.
 *   2. Else → intersect suburb band with bucket, then cap (legacy).
 *
 * In both paths, the final width is guaranteed to be ≤ maxDisplayWidth
 * (A$100k / ₹10L / $150/wk / ₹10k/mo) so the number feels like a
 * decision, not a search.
 */
export function clampedPriceRange(
  ctx: BudgetFormatCtx,
  suburbBand: BudgetBand,
  userBucket: BudgetBucket,
  priceData?: SuburbPriceData
): NumRange | null {
  // 1 — median-centred window (preferred when we have real data).
  const median = priceData ? pickMedian(ctx, priceData) : null;

  // Choose the anchor range: median window if available, else suburb band.
  const anchor: NumRange = median != null
    ? {
        lo: Math.max(0, median - medianHalfWidth(ctx)),
        hi: median + medianHalfWidth(ctx),
      }
    : bandBounds(ctx, suburbBand);

  // "Any" bucket → show the anchor range directly (still capped below).
  if (userBucket === "any") {
    const maxW = maxDisplayWidth(ctx);
    if (anchor.hi - anchor.lo > maxW) {
      // Centre the capped window on the median when we have one; otherwise
      // keep the anchor's low edge (legacy behaviour for band-only suburbs).
      if (median != null) {
        const half = maxW / 2;
        return { lo: Math.max(0, median - half), hi: Math.max(0, median - half) + maxW };
      }
      return { lo: anchor.lo, hi: anchor.lo + maxW };
    }
    return anchor;
  }

  // 2 — intersect anchor with the user's bucket.
  const bucket = budgetBucketDef(ctx, userBucket);
  const lo = Math.max(anchor.lo, bucket.lo);
  const hi = Math.min(anchor.hi, bucket.hi);
  if (hi <= lo) {
    // No overlap. When we have a researched median, the caller almost
    // always wants to show *something truthful* — so fall back to the
    // median window (capped) rather than returning null. This prevents
    // a wealthy suburb disappearing from a wealthy user's shortlist just
    // because our bucket edges don't align with that suburb's median.
    if (median != null) {
      const maxW = maxDisplayWidth(ctx);
      const half = maxW / 2;
      return { lo: Math.max(0, median - half), hi: Math.max(0, median - half) + maxW };
    }
    return null;
  }
  const maxW = maxDisplayWidth(ctx);
  if (hi - lo > maxW) {
    // When capping, prefer to keep the median inside the visible window.
    if (median != null && median >= lo && median <= hi) {
      const half = maxW / 2;
      const capLo = Math.max(lo, median - half);
      const capHi = Math.min(hi, capLo + maxW);
      return { lo: Math.max(lo, capHi - maxW), hi: capHi };
    }
    return { lo, hi: lo + maxW };
  }
  return { lo, hi };
}

/* -------------------------------------------------------------------------- */
/*  Label helpers                                                             */
/* -------------------------------------------------------------------------- */

/** Return the numeric label for a (ctx, band) combination. */
export function budgetRangeLabel(ctx: BudgetFormatCtx, band: BudgetBand): string {
  if (band === "none") return "Any";
  // "any" type defaults to house numbers — that's the dominant pocket users
  // start from. Tapping Apartment softens the numbers.
  const effType: "house" | "apartment" = ctx.type === "apartment" ? "apartment" : "house";
  const city = RANGES[ctx.city];
  const mode = city[ctx.mode];
  const tier = mode[effType];
  return tier[band];
}

/**
 * Short label used on the pill. Same as `budgetRangeLabel` but with a
 * leading tier word ("Starter" / "Comfortable" / "Premium") dropped — the
 * pill is tight and the number carries the meaning.
 */
export function budgetPillLabel(ctx: BudgetFormatCtx, band: BudgetBand): string {
  return budgetRangeLabel(ctx, band);
}

/**
 * One-line summary for the shortlist sub-header: "MEL · Buy · House · A$900k–1.5M".
 * Used only when Band !== "none"; callers skip it otherwise.
 */
export function budgetSummaryLine(ctx: BudgetFormatCtx, band: BudgetBand): string {
  if (band === "none") return "";
  const typeLabel =
    ctx.type === "house" ? "House" : ctx.type === "apartment" ? "Apartment" : "Any type";
  const modeLabel = ctx.mode === "rent" ? "Rent" : "Buy";
  return `${modeLabel} · ${typeLabel} · ${budgetRangeLabel(ctx, band)}`;
}
